import { Link, Navigate, useLocation } from 'react-router-dom';
import { useCurrentContext } from '../lib/useCurrentContext.jsx';
import './AssessmentResults.css';

// The assessment results screen. Shown after the user completes the
// assessment, before they pick their goals.
//
// Identity-source-agnostic (Phase 2C): reads from useCurrentContext
// so the same component serves community residents (data under
// /brain_health/accounts/) and individual subscribers (under /users/).
// The page is read-only — no writes, so no path-aware logic needed
// beyond reading from the unified record.
//
// If the user lands here without a saved assessment, redirect them
// back to the assessment.
export default function AssessmentResults() {
  const { record: account, isPreview } = useCurrentContext();
  const location = useLocation();

  // PREVIEW — remove when no longer needed.
  // In preview, results come from router state (the assessment never
  // saved to Firebase). The "Next" link points at the preview goals
  // page so the walkthrough continues.
  const previewState = isPreview ? location.state : null;
  const nextGoalsPath = isPreview ? '/assessment/preview/goals' : '/onboarding/goals';
  const backToAssessmentPath = isPreview
    ? '/assessment/preview'
    : '/onboarding/assessment';

  if (!account) {
    return (
      <div className="container results-page">
        <p className="results-meta">Loading…</p>
      </div>
    );
  }

  // In preview, prefer the router-state results; otherwise read the
  // saved record. (PREVIEW branch in the next two lines.)
  const current = isPreview
    ? (previewState && previewState.score != null ? previewState : null)
    : account.assessment?.current;
  if (!current) {
    return <Navigate to={backToAssessmentPath} replace />;
  }

  const firstName = (account.display_name || '').split(' ')[0] || 'there';

  return (
    <div className="container results-page">
      <span className="eyebrow fade-in-1">Assessment Complete</span>
      <h1 className="results-title fade-in-2">
        Thank you, <em>{firstName}</em>.
      </h1>

      <div className="results-card fade-in-3">
        {/* Per Dr. Winningham: residents see a single reassuring
            message, NOT a score or a cognitive level. The level is
            still computed and saved (it drives game difficulty), but
            showing it can feel like a diagnosis or a threat to one's
            level of care, which discourages people from doing the
            assessment at all. */}
        <p
          className="results-message"
          style={{
            fontFamily: 'var(--font-serif, Georgia, serif)',
            fontSize: '1.3rem',
            lineHeight: 1.6,
            color: 'var(--plum, #33143E)',
            margin: 0,
          }}
        >
          We'll use your assessment results to suggest activities
          designed to exercise, and possibly improve, certain mental
          abilities.
        </p>
      </div>

      <div className="results-cta fade-in-4">
        <Link to={nextGoalsPath} className="btn">
          Next: choose your goals →
        </Link>
      </div>

      {/* PREVIEW — remove when no longer needed. */}
      {isPreview ? (
        <p className="results-note" style={{ fontStyle: 'italic' }}>
          Preview mode — nothing was saved.
        </p>
      ) : (
        <p className="results-note">
          Your results stay private to you and your community's care team.
        </p>
      )}
    </div>
  );
}
